import React, { useState, useEffect } from "react";
import RecipeDetailEdit from "../components/RecipeDetailEdit";
import Button from "../components/Button";
import { uploadImage, saveRecipe, addRecipe } from "../services/recipeServices";
import { getImage } from "../stores/imageStore";
import ErrorMessage from "./ErrorMessage";
import { useUser } from "../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { serverTimestamp } from "firebase/firestore";

const RecipeEditor = ({ initialRecipe, fetchRecipe, onSaveComplete, pageTitle }) => {
  const [recipe, setRecipe] = useState(initialRecipe || null);
  const [imageUrl, setImageUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(!initialRecipe);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (fetchRecipe) {
      const loadRecipe = async () => {
        try {
          const fetchedRecipe = await fetchRecipe();

          if (fetchedRecipe.userId !== user.id) {
            navigate(`/recipe/${fetchedRecipe.id}`);
          }

          setRecipe(fetchedRecipe);

          if (fetchedRecipe.images?.length > 0) {
            const url = await getImage(fetchedRecipe.images[0]);
            setImageUrl(url);
          }
        } catch (error) {
          console.error("Error loading recipe:", error);
        } finally {
          setLoading(false);
        }
      };

      loadRecipe();
    } else {
      setLoading(false); // No fetch logic needed for `AddRecipe`
    }
  }, [fetchRecipe, navigate, user.id]);

  useEffect(() => {
    if (errorMessage) {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      })
    }
  }, [errorMessage])

  const validateRecipe = () => {
    const errors = {};

    if (!recipe.title || recipe.title.trim() === "") {
      errors.title = "Recipe title is required";
    }
    if (!recipe.serves || recipe.serves <= 0) {
      errors.serves = "Number of servings must be greater than 0";
    }
    if (!recipe.source || recipe.source.trim() === "") {
      errors.source = "Recipe source is required";
    }
    if (!recipe.cookTime) {
      errors.cookTime = "Cook Time is required";
    } else if (!recipe.cookTime || recipe.cookTime < 0) {
      errors.cookTime = "Cook Time must not be negative";
    }
    if (!recipe.description || recipe.description.trim() === "") {
      errors.description = "Recipe description is required";
    }

    // Validate Ingredients
    recipe.ingredients.forEach((section, sectionIndex) => {
      let hasSectionErrors = false;

      section.items.forEach((item, itemIndex) => {
        if (!item.name || item.name.trim() === "") {
          errors.ingredients = errors.ingredients || {};
          errors.ingredients[sectionIndex] = errors.ingredients[sectionIndex] || { section: section.Section, items: {} };
          errors.ingredients[sectionIndex].items[itemIndex] = errors.ingredients[sectionIndex].items[itemIndex] || {};
          errors.ingredients[sectionIndex].items[itemIndex].name = "Ingredient name cannot be empty.";
          hasSectionErrors = true;
        }
      });

      if (section.items.length === 0 && !hasSectionErrors) {
        errors.ingredients = errors.ingredients || {};
        errors.ingredients[sectionIndex] = { section: section.Section, items: "Section must have at least one item." };
      }
    });

    // Validate instructions
    for (const [index, instruction] of recipe.instructions.entries()) {
      if (!instruction.text || instruction.text.trim() === "") {
        errors.instructions = errors.instructions || {};
        errors.instructions[index] = "Instruction cannot be empty."
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const updateImage = (file) => {
    if (file) {
      setImageUrl(URL.createObjectURL(file));
      setFile(file);
    } else {
      setImageUrl(null);
    }
  };

  const updateRecipe = (field, value) => {
    setRecipe((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!validateRecipe()) {
      setErrorMessage("Please correct the errors before saving");
      return;
    }

    try {
      setIsSaving(true);
      let updatedImagePath = recipe.images?.[0] || "";

      // If there is a new file -> upload it to storage
      // If the imageUrl is null then there is no image or the image was removed
      if (file) {
        updatedImagePath = await uploadImage(file);
      } else if (imageUrl === null) {
        updatedImagePath = null;
      }

      const updatedRecipe = {
        ...recipe,
        images: updatedImagePath ? [updatedImagePath] : [],
        created: recipe.created ? recipe.created : serverTimestamp(),
        updated: serverTimestamp()
      };

      if (updatedRecipe.id) {
        await saveRecipe(updatedRecipe);
      } else {
        await addRecipe(updatedRecipe);
      }

      setIsSaving(false);
      onSaveComplete(); // Handle post-save navigation or state
    } catch (error) {
      console.error("Error saving recipe:", error);
      setErrorMessage(error.message || "Failed to save the recipe.")
      setIsSaving(false)
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <p className="text-2xl text-gray-500">Loading recipe...</p>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="p-6">
        <h1 className="text-2xl text-red-500">{pageTitle} not found</h1>
        <Button type="primary" onClick={onSaveComplete}>
          Back to Recipes
        </Button>
      </div>
    );
  }

  return (
    <div>
      {errorMessage && (
        <ErrorMessage message={errorMessage} />
      )}
      <RecipeDetailEdit
        recipe={recipe}
        imageUrl={imageUrl}
        mode="edit"
        onRecipeChange={updateRecipe}
        onImageChange={updateImage}
        onSave={handleSave}
        isSaving={isSaving}
        fieldErrors={fieldErrors}
      />
    </div>
  );
};

export default RecipeEditor;
